/**
 * ChecklistHeading — A section header within the checklist.
 *
 * Uses `subType = ChecklistSubType.Heading (1)` to distinguish from items.
 * No checkbox — just bold text + drag handle.
 *
 * Features:
 * - `⌘⇧H` → convert back to regular item
 * - `Enter` → create a new regular item below (not another section)
 * - Arrow keys → navigate between items/sections seamlessly
 * - Selection support (Mod+Click, ⌘A)
 *
 * Multi-user optimized with per-item `useSuspendingLiveQuery` + React.memo.
 */

import {
  memo,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type FormEvent,
} from "react"
import { useTranslation } from "react-i18next"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import { updateChecklistTitle } from "@/db/operations"
import { useChecklistItemData } from "@/features/task-detail/hooks/useChecklistItems"
import {
  getCursorOffset,
  setCursorOffset,
  getCursorRect,
  isCursorOnFirstLine,
  isCursorOnLastLine,
  hasNonTextChildren,
  stripLineBreaks,
} from "./cursorUtils"
import ThreeDotsIcon from "@/shared/icons/totodoicons/stroke/three-dots-vertical.svg?react"
import { parseClipboardLines, type ParsedLine } from "./parseClipboardLines"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUTO_SAVE_DELAY = 3_000


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChecklistHeadingProps {
  id: string
  /** Container's merged (optimistic) item — used as fallback before DB query lands */
  optimisticItem?: DbObject
  isDragOverlay?: boolean
  /**
   * True while ANY row in this checklist is being dragged.
   *
   * Used to drop contentEditable for the duration of the drag. On iOS the
   * rows kept their chrome (checkbox, image icon, drag handle) but their TEXT
   * vanished mid-drag — only the contentEditable layer failed to repaint while
   * dnd-kit continuously updated the ancestor transform. The row stays fully
   * visible; it is only temporarily not editable, which costs nothing because
   * you cannot type while dragging.
   */
  isDragActive?: boolean
  /** Whether this item is in the multi-selection set */
  isSelected?: boolean
  /** Whether this item is the first in a contiguous selection group */
  isFirstSelected?: boolean
  /** Whether this item is the last in a contiguous selection group */
  isLastSelected?: boolean
  /** Whether this item is being dragged as part of a multi-selection (not the primary) */
  isDraggedAway?: boolean
  onDeleteAndFocusPrevious?: (id: string) => void
  /** Enter on section → create a new regular item below */
  onSplitAtCursor?: (
    itemId: string,
    itemIndex: number,
    textBefore: string,
    textAfter: string,
  ) => void
  /** ArrowUp/Left at top/start → navigate to previous item */
  onNavigatePrevious?: (id: string, cursorX: number) => void
  /** ArrowDown/Right at bottom/end → navigate to next item */
  onNavigateNext?: (id: string, cursorX: number) => void
  /** Notify container when contentEditable receives focus */
  onFocusItem?: (id: string) => void
  /** Notify container of modifier-click for multi-selection */
  onSelectItem?: (
    id: string,
    modifiers: { meta: boolean; shift: boolean },
  ) => void
  /** ⌘⇧H → convert section back to regular item */
  onConvertToItem?: (id: string, title?: string) => void
  /** Shift+Arrow → extend checklist selection */
  onShiftArrow?: (id: string, direction: "up" | "down") => void
  /** Multi-line paste → create multiple checklist items */
  onPasteItems?: (id: string, items: ParsedLine[]) => void
  /** Tab → indent item (increase depth) */
  onIndent?: (id: string) => void
  /** Shift+Tab → outdent item (decrease depth) */
  onOutdent?: (id: string) => void
  /** Whether this section is collapsed (items hidden) */
  isCollapsed?: boolean
  /** Pre-initialize as editing (prevents bg-accent flicker on component swap) */
  startFocused?: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChecklistHeading = memo(
  function ChecklistHeading({
    id,
    optimisticItem,
    isDragOverlay,
    isDragActive,
    isSelected,
    isFirstSelected,
    isLastSelected,
    isDraggedAway,
    onDeleteAndFocusPrevious,
    onSplitAtCursor,
    onNavigatePrevious,
    onNavigateNext,
    onFocusItem,
    onSelectItem,
    onConvertToItem,
    onShiftArrow,
    onPasteItems,
    onIndent,
    onOutdent,
    isCollapsed,
    startFocused,
  }: ChecklistHeadingProps) {
    const [dbItem] = useChecklistItemData(id)
    const { t } = useTranslation()
    // Prefer container's optimistic data (has pending overrides applied)
    // over per-item DB query (may be stale during async transactions).
    const item = optimisticItem ?? dbItem
    const titleRef = useRef<HTMLDivElement>(null)
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isEditing, setIsEditing] = useState(startFocused ?? false)

    // Track text just flushed to DB — prevents useLayoutEffect from
    // reverting the DOM to stale item.title between blur and DB update.
    const savedTextRef = useRef<string | null>(null)
    useEffect(() => {
      if (
        savedTextRef.current !== null &&
        (item?.title ?? "") === savedTextRef.current
      ) {
        savedTextRef.current = null
      }
    }, [item?.title])

    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id, disabled: isDragOverlay })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.3 : 1,
    }

    useEffect(() => {
      return () => {
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
      }
    }, [])

    // Sync DOM content — update whenever item.title changes externally
    useLayoutEffect(() => {
      if (!titleRef.current || !item) return
      const domText = titleRef.current.textContent ?? ""
      const dbTitle = item.title ?? ""

      // If title matches DOM already, nothing to do
      if (domText === dbTitle) return

      // A save is still in flight — the DOM holds exactly what we just wrote
      // and the DB hasn't caught up. Overwriting here blanks the row until
      // the write lands. See the matching guard in ChecklistItem.
      if (savedTextRef.current !== null && domText === savedTextRef.current) return

      // External change (undo/sync/split) — always update DOM
      titleRef.current.textContent = dbTitle
      savedTextRef.current = null
    }, [item?.title, isEditing])

    // ── Save helpers ────────────────────────────────────────────────────

    const flushSave = useCallback(() => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current)
        autoSaveTimer.current = null
      }
      if (!item?.id || !titleRef.current) return
      const text = titleRef.current.textContent ?? ""
      if (text !== (item.title ?? "")) {
        savedTextRef.current = text
        updateChecklistTitle(item.id, text)
      }
    }, [item?.id, item?.title])

    const handleBlur = useCallback(() => {
      setIsEditing(false)
      flushSave()
    }, [flushSave])

    const handleInput = useCallback((e: FormEvent<HTMLDivElement>) => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
      // A row is one line: drop a break the browser inserted on Enter,
      // whenever it arrives (see stripLineBreaks).
      if (titleRef.current && !(e.nativeEvent as InputEvent).isComposing) {
        stripLineBreaks(titleRef.current)
      }
      autoSaveTimer.current = setTimeout(() => {
        if (!item?.id || !titleRef.current) return
        const newTitle = titleRef.current.textContent?.trim() ?? ""
        if (newTitle !== item.title) {
          updateChecklistTitle(item.id, newTitle)
        }
      }, AUTO_SAVE_DELAY)
    }, [item?.id, item?.title])

    // ── Keyboard ────────────────────────────────────────────────────────

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        // ── Tab / Shift+Tab: indent / outdent ──
        if (e.key === "Tab") {
          e.preventDefault()
          if (!item?.id) return
          if (e.shiftKey) {
            onOutdent?.(item.id)
          } else {
            onIndent?.(item.id)
          }
          return
        }

        // ── Enter: split ──
        // MOB-02: deliberately NOT calling e.preventDefault() on the normal
        // path — see ChecklistItem's Enter handler for the full rationale
        // (iOS's capitalization/autocorrect state machine needs to observe
        // the real Enter keypress reach the DOM). Cursor state is read
        // synchronously below, before the browser's default action mutates
        // the DOM; the split + DOM repair is deferred to the next frame.
        if (e.key === "Enter") {
          // IME composition guard — without preventDefault, an Enter that
          // commits a composition would otherwise also trigger a split.
          if (e.nativeEvent.isComposing) return

          if (!titleRef.current || !item?.id || item.index === undefined) {
            // No handler will run to repair a stray newline — block it.
            e.preventDefault()
            return
          }

          // Flush any pending debounced save before splitting — Mode A
          // (below) does not itself persist the current section's title,
          // so relying on the 3s debounce firing later risks losing text
          // typed just before Enter. Mirrors ChecklistItem's Enter handler.
          flushSave()

          const el = titleRef.current
          const itemId = item.id
          const itemIndex = item.index
          const fullText = el.textContent ?? ""
          const offset = getCursorOffset(el)
          const before = fullText.slice(0, offset)
          const after = fullText.slice(offset)

          requestAnimationFrame(() => {
            if (!el.isConnected) return

            // The browser's own newline insertion may have queued an
            // autosave via onInput — the split logic below is the source
            // of truth for what gets persisted, so drop it.
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }

            if (after === "") {
              // Mode A: Cursor at END — current section stays unchanged.
              // Container creates a new, empty regular item (not another
              // section) BELOW and moves cursor there. Repair the
              // browser's inserted newline/<br>, if any — no-op when the
              // browser didn't touch the DOM.
              if ((el.textContent ?? "") !== before || hasNonTextChildren(el)) {
                el.textContent = before
              }
              // Same guard as ChecklistItem's Mode A: now that this branch
              // touches the DOM, useLayoutEffect would otherwise revert it
              // to the stale item.title until flushSave's write lands,
              // blanking the row for over a second.
              savedTextRef.current = before
              onSplitAtCursor?.(itemId, itemIndex, before, after)
            } else {
              // Mode B: Cursor in MIDDLE — current section keeps textAfter
              // but becomes a regular item (container downgrades subType).
              // A new section ABOVE gets textBefore.
              if ((el.textContent ?? "") !== after || hasNonTextChildren(el)) {
                el.textContent = after
              }
              setCursorOffset(el, 0)

              // Guard against useLayoutEffect restoring stale item.title
              savedTextRef.current = after

              onSplitAtCursor?.(itemId, itemIndex, before, after)
            }
          })
          return
        }

        // ── ⌘⇧H: convert back to regular item ──
        if (
          e.code === "KeyH" &&
          (e.metaKey || e.ctrlKey) &&
          e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) {
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            flushSave()
            onConvertToItem?.(item.id)
          }
          return
        }

        // ── Escape: revert + blur ──
        if (e.key === "Escape") {
          e.preventDefault()
          if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current)
            autoSaveTimer.current = null
          }
          if (titleRef.current && item) {
            titleRef.current.textContent = item.title
          }
          titleRef.current?.blur()
          return
        }

        // ── Arrow navigation across items ──
        if (
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "ArrowUp" ||
          e.key === "ArrowDown"
        ) {
          if (!titleRef.current || !item?.id) return

          // Shift+Arrow at edge → item-level selection
          if (e.shiftKey) {
            if (e.key === "ArrowUp" && isCursorOnFirstLine(titleRef.current)) {
              e.preventDefault()
              onShiftArrow?.(item.id, "up")
              return
            }
            if (e.key === "ArrowDown" && isCursorOnLastLine(titleRef.current)) {
              e.preventDefault()
              onShiftArrow?.(item.id, "down")
              return
            }
            return // Let browser handle text selection within element
          }

          const fullText = titleRef.current.textContent ?? ""
          const offset = getCursorOffset(titleRef.current)
          const cursorRect = getCursorRect()
          const cursorX = cursorRect?.left ?? 0

          if (e.key === "ArrowLeft" && offset === 0) {
            e.preventDefault()
            onNavigatePrevious?.(item.id, Infinity)
            return
          }
          if (e.key === "ArrowRight" && offset === fullText.length) {
            e.preventDefault()
            onNavigateNext?.(item.id, 0)
            return
          }
          if (e.key === "ArrowUp" && isCursorOnFirstLine(titleRef.current)) {
            e.preventDefault()
            onNavigatePrevious?.(item.id, cursorX)
            return
          }
          if (e.key === "ArrowDown" && isCursorOnLastLine(titleRef.current)) {
            e.preventDefault()
            onNavigateNext?.(item.id, cursorX)
            return
          }
          return
        }

        // ── Backspace/Delete: merge or delete ──
        if (e.key === "Backspace" || e.key === "Delete") {
          if (!titleRef.current || !item?.id) return
          const sel = window.getSelection()
          if (sel && !sel.isCollapsed) return

          const content = titleRef.current.textContent?.trim() ?? ""
          if (content === "") {
            e.preventDefault()
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            onDeleteAndFocusPrevious?.(item.id)
          }
        }
      },
      [
        item,
        flushSave,
        onSplitAtCursor,
        onDeleteAndFocusPrevious,
        onNavigatePrevious,
        onNavigateNext,
        onConvertToItem,
        onShiftArrow,
        onIndent,
        onOutdent,
      ],
    )

    // Paste: single line → insert; multi-line → first line modifies Anchor, rest create below.
    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault()
        const text = e.clipboardData.getData("text/plain")
        if (!text || !item?.id) return

        const rawLines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
        if (rawLines.length <= 1) {
          document.execCommand("insertText", false, text.replace(/\n/g, ""))
          return
        }

        // Multi-line: first line → current section, rest → new items
        const firstRawLine = rawLines[0]!
        const currentText = titleRef.current?.textContent ?? ""

        if (currentText.trim() === "") {
          // Empty section: parse first line and set title
          const firstParsed = parseClipboardLines(firstRawLine)[0]
          if (firstParsed && titleRef.current) {
            titleRef.current.textContent = firstParsed.title
            savedTextRef.current = firstParsed.title
            updateChecklistTitle(item.id, firstParsed.title)
            // If first line is a task type, convert section → item
            if (firstParsed.type === "task") {
              onConvertToItem?.(item.id, firstParsed.title)
            }
          }
        } else {
          // Non-empty: append raw first line at cursor
          document.execCommand("insertText", false, firstRawLine)
        }

        // Remaining lines → parse and create below
        const remainingText = rawLines.slice(1).join("\n")
        const parsedLines = parseClipboardLines(remainingText)
        if (parsedLines.length > 0) {
          onPasteItems?.(item.id, parsedLines)
        }
      },
      [item?.id, onPasteItems, onConvertToItem],
    )

    // Modifier-click on the row → multi-select
    const handleRowMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault()
          onSelectItem?.(id, {
            meta: e.metaKey || e.ctrlKey,
            shift: e.shiftKey,
          })
        }
      },
      [id, onSelectItem],
    )

    if (!item) return null

    return (
      <div
        ref={setNodeRef}
        style={{ ...style, paddingLeft: (item.depth ?? 0) * 24 + 6 }}
        {...attributes}
        data-checklist-id={id}
        onMouseDown={handleRowMouseDown}
        className={cn(
          "group flex items-start gap-2 py-1.5 pr-1",
          "transition-colors duration-150",
          // Rounding: same logic as ChecklistItem for contiguous selection
          isSelected && !isFirstSelected && !isLastSelected && !isDragOverlay
            ? "rounded-none"
            : isSelected && !isFirstSelected && !isDragOverlay
              ? "rounded-b-md rounded-t-none"
              : isSelected && !isLastSelected && !isDragOverlay
                ? "rounded-t-md rounded-b-none"
                : "rounded-md",
          isEditing && !isDragOverlay && "bg-accent/50",
          isSelected && !isDragOverlay && "bg-primary/10",
          isDraggedAway && "opacity-30 transition-opacity duration-200",
          isDragOverlay && "bg-card shadow-md",
        )}
      >
        {/* Collapse indicator */}
        {isCollapsed && (
          <span className="mt-0.5 shrink-0 text-xs text-muted-foreground select-none" aria-label="Collapsed">
            ▸
          </span>
        )}

        {/* Editable section title */}
        <div
          ref={titleRef}
          contentEditable={!isDragOverlay && !isDragActive}
          suppressContentEditableWarning
          role="textbox"
          aria-label={t("Checklist section")}
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck={true}
          enterKeyHint="enter"
          className={cn(
            "min-w-0 flex-1 text-sm font-semibold outline-none leading-snug",
            "text-foreground/80",
          )}
          onMouseDown={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey) {
              e.preventDefault()
              e.stopPropagation()
              onSelectItem?.(id, {
                meta: e.metaKey || e.ctrlKey,
                shift: e.shiftKey,
              })
            }
          }}
          onFocus={() => {
            setIsEditing(true)
            onFocusItem?.(id)
          }}
          onBlur={handleBlur}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />

        {/* Drag handle */}
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...listeners}
          className={cn(
            "mt-0.5 shrink-0 cursor-grab touch-none",
            // Permanently visible on touch (any-pointer: coarse covers touch
            // laptops too, where the primary pointer is a fine trackpad but
            // touch is also available); hover-only on mouse/trackpad devices.
            "opacity-0 any-pointer-coarse:opacity-40 group-hover:opacity-40 transition-opacity duration-150",
            isDragOverlay && "opacity-40",
          )}
          aria-label={t("Drag to reorder")}
          tabIndex={-1}
        >
          <ThreeDotsIcon
            width={14}
            height={14}
            style={{ color: "var(--muted-foreground)" }}
          />
        </button>
      </div>
    )
  },
  (prev, next) =>
    prev.id === next.id &&
    prev.isDragOverlay === next.isDragOverlay &&
    prev.isDragActive === next.isDragActive &&
    prev.isSelected === next.isSelected &&
    prev.isFirstSelected === next.isFirstSelected &&
    prev.isLastSelected === next.isLastSelected &&
    prev.isDraggedAway === next.isDraggedAway &&
    prev.isCollapsed === next.isCollapsed &&
    prev.optimisticItem?.depth === next.optimisticItem?.depth &&
    prev.optimisticItem?.title === next.optimisticItem?.title,
)
